import { useState } from "react";
import { Reveal, MaskLineInView } from "./Reveal";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { HeartHandshake, BookOpen, Loader2 } from "lucide-react";

const field = "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-sm text-white placeholder:text-[#7A708A] outline-none focus:border-gold/50 transition-colors";

export default function Connect() {
  return (
    <section id="connect" className="relative py-28 md:py-40 bg-[#0A0710]">
      <div className="absolute left-1/2 -translate-x-1/2 top-20 w-[600px] h-[400px] rounded-full bg-[#6A0DAD]/15 blur-[140px]" />
      <div className="relative max-w-5xl mx-auto px-5 sm:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <Reveal><span className="section-label">04 — Come to the Water</span></Reveal>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.02] mt-5 text-white">
            <MaskLineInView>Come thirsty.</MaskLineInView>
            <MaskLineInView delay={0.1}><span className="italic text-gold">Come home to Jesus.</span></MaskLineInView>
          </h2>
          <Reveal delay={0.2}>
            <p className="text-lg text-[#B4ADC1] font-light mt-6 leading-relaxed">
              Request confidential prayer or begin your free Bible journey. There is no pressure — only living water, apostolic truth, and a family for you.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="glass rounded-3xl p-6 sm:p-10 mt-14">
            <Tabs defaultValue="prayer" className="w-full">
              <TabsList className="grid grid-cols-2 w-full bg-white/5 rounded-full p-1 h-auto mb-8">
                <TabsTrigger value="prayer" data-testid="tab-prayer" className="rounded-full py-2.5 data-[state=active]:bg-gold data-[state=active]:text-black text-[#B4ADC1] gap-2">
                  <HeartHandshake className="w-4 h-4" /> Request Prayer
                </TabsTrigger>
                <TabsTrigger value="study" data-testid="tab-study" className="rounded-full py-2.5 data-[state=active]:bg-gold data-[state=active]:text-black text-[#B4ADC1] gap-2">
                  <BookOpen className="w-4 h-4" /> Bible Journey
                </TabsTrigger>
              </TabsList>
              <TabsContent value="prayer"><PrayerForm /></TabsContent>
              <TabsContent value="study"><StudyForm /></TabsContent>
            </Tabs>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function PrayerForm() {
  const [f, setF] = useState({ first_name: "", contact: "", contact_method: "either", prayer_need: "", want_bible_study: false, want_transport: false });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.first_name || !f.contact || !f.prayer_need) { toast.error("Please share your name, contact, and prayer need."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/prayer-requests", f);
      toast.success(data.message);
      setF({ first_name: "", contact: "", contact_method: "either", prayer_need: "", want_bible_study: false, want_transport: false });
    } catch { toast.error("Something went wrong. Please call 726-247-5926."); }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="prayer-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <Input className={field} placeholder="First name" value={f.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="prayer-name" />
        <Input className={field} placeholder="Mobile number or email" value={f.contact} onChange={(e) => set("contact", e.target.value)} data-testid="prayer-contact" />
      </div>
      <Textarea className={`${field} min-h-28`} placeholder="What would you like us to pray for? (Held in confidence)" value={f.prayer_need} onChange={(e) => set("prayer_need", e.target.value)} data-testid="prayer-need" />
      <div className="flex flex-col sm:flex-row gap-4 pt-1">
        <label className="flex items-center gap-3 text-sm text-[#B4ADC1] cursor-pointer">
          <Checkbox checked={f.want_bible_study} onCheckedChange={(v) => set("want_bible_study", !!v)} data-testid="prayer-study-check" /> I'd like a Bible study
        </label>
        <label className="flex items-center gap-3 text-sm text-[#B4ADC1] cursor-pointer">
          <Checkbox checked={f.want_transport} onCheckedChange={(v) => set("want_transport", !!v)} data-testid="prayer-transport-check" /> I need transportation info
        </label>
      </div>
      <button type="submit" disabled={busy} className="w-full py-4 rounded-full bg-gold text-black font-medium hover:bg-[#e6c14e] transition-colors flex items-center justify-center gap-2 disabled:opacity-50" data-testid="prayer-submit">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Request Confidential Prayer
      </button>
    </form>
  );
}

function StudyForm() {
  const [f, setF] = useState({ first_name: "", contact: "", preferred_day: "", format: "online" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.first_name || !f.contact) { toast.error("Please share your name and contact."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/bible-study", f);
      toast.success(data.message);
      setF({ first_name: "", contact: "", preferred_day: "", format: "online" });
    } catch { toast.error("Something went wrong. Please call 726-247-5926."); }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="study-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <Input className={field} placeholder="First name" value={f.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="study-name" />
        <Input className={field} placeholder="Mobile number or email" value={f.contact} onChange={(e) => set("contact", e.target.value)} data-testid="study-contact" />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Input className={field} placeholder="Preferred day (optional)" value={f.preferred_day} onChange={(e) => set("preferred_day", e.target.value)} data-testid="study-day" />
        <div className="flex bg-white/5 border border-white/10 rounded-xl p-1">
          {["online", "in-person"].map((opt) => (
            <button type="button" key={opt} onClick={() => set("format", opt)} className={`flex-1 rounded-lg py-2.5 text-sm capitalize transition-colors ${f.format === opt ? "bg-gold text-black" : "text-[#B4ADC1]"}`} data-testid={`study-format-${opt}`}>
              {opt}
            </button>
          ))}
        </div>
      </div>
      <button type="submit" disabled={busy} className="w-full py-4 rounded-full bg-gold text-black font-medium hover:bg-[#e6c14e] transition-colors flex items-center justify-center gap-2 disabled:opacity-50" data-testid="study-submit">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Begin the Free 7-Day Journey
      </button>
    </form>
  );
}
